import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { LocalStorage } from 'angular-web-storage';

import {
  CustomInbox,
  CustomInboxItem,
  CustomInboxItemFilters,
  CustomInboxStatus,
  getCustomInboxStatusDisplay,
  loadCustomInboxItemFiltersFromGetParams
} from '../../../../../models/custom-inbox';
import { ListResponse } from '../../../../../models/list-response';
import { SavedReportViewReportType } from '../../../../../models/saved-report-view';
import * as ExportColumns from '../../../../../models/exports/custom-inbox-items';
import * as ReportColumns from '../../../../../models/report-columns/custom-inbox-items';
import { columnsOfType } from '../../../../../reporting-core/column-types';
import { ReportCellType } from '../../../../../reporting-core/cell-format';
import {
  CallbackResult,
  CallbackResultStatus,
  dateStringFromISO8601,
  getActiveDateRangeFilterText,
  propExists,
  timeStringFromISO8601
} from '../../../../../utils';
import { AccountService } from '../../../../../services/account/account.service';
import { InboxService } from '../../../../../services/inbox/inbox.service';
import { OrganizationConfigService } from '../../../../../services/organization-config/organization-config.service';
import { CustomInboxService } from '../../../../../services/custom-inbox/custom-inbox.service';
import {
  SavedReportViewService
} from '../../../../../services/saved-report-view/saved-report-view.service';
import { ReportBaseDirective } from '../../../report-base.directive';
import {
  ApiCollectionExportModalComponent
} from '../../modals/api-collection-export-modal/api-collection-export-modal.component';
import { ViewCustomInboxItemComponent } from './modals/view-custom-inbox-item/view-custom-inbox-item.component';
import {
  CustomInboxItemFiltersModalComponent
} from './modals/custom-inbox-item-filters-modal/custom-inbox-item-filters-modal.component';


const EXPORT_PAGE_SIZE = 1000;


@Component({
  selector: 'app-inbox-custom',
  templateUrl: './inbox-custom.component.html',
  styleUrls: ['./inbox-custom.component.css']
})
export class InboxCustomComponent extends ReportBaseDirective<CustomInboxItemFilters> implements OnInit {
  // Parameters required by ReportBaseDirective.
  public filters: CustomInboxItemFilters = {};

  protected readonly DefaultColumns = ReportColumns.DEFAULT_COLUMNS;
  protected readonly ReportColumnNames = ReportColumns.REPORT_COLUMN_NAMES;
  protected readonly ColumnHelpText = ExportColumns.COLUMN_HELP_TEXT;
  protected readonly ReportColumnHeaderOverrides = ReportColumns.COLUMN_HEADER_OVERRIDES;
  // `is_resolved` is NOT excluded: a saved view may remember "Resolved". An
  // absent value normalizes to unresolved in `loadReport()`.
  protected readonly SavedViewExcludedProps = [
    'created_at_min',
    'created_at_max',
  ];
  protected readonly ReportType = SavedReportViewReportType.customInboxItems;

  protected loadFiltersFromGetParams = loadCustomInboxItemFiltersFromGetParams;

  // Parameters specific to this component.
  @LocalStorage() public customInboxItemsPageSize;

  public inboxResponse: ListResponse<CustomInboxItem>;
  // All statuses, and the active subset the selects offer. A saved view or URL
  // referencing a retired inbox needs the full list to render a pinned,
  // labelled option instead of a blank select (removed-record convention).
  public inboxList: CustomInbox[];
  public activeInboxes: CustomInbox[];

  public loadingInbox = true;
  public loadingInboxList = true;

  // The most recent result returned from a modal.
  public modalResult: CallbackResult = null;

  // The number of item list requests. Used to ensure that only the most recent
  // request is displayed, in the event of concurrent requests (due to, e.g.,
  // filtering).
  private listRequestCount = 0;

  // Create instance references to lookup functions and enums so they may be
  // called in the template.
  public readonly CallbackResultStatus = CallbackResultStatus;
  public readonly ReportColumns = ReportColumns;
  public readonly ExportColumns = ExportColumns;
  public readonly timeStringFromISO8601 = timeStringFromISO8601;
  public readonly dateStringFromISO8601 = dateStringFromISO8601;
  public readonly propExists = propExists;

  public readonly ColumnsWithCustomHTML = columnsOfType(ReportColumns.COLUMN_TYPES, ReportCellType.custom);
  public readonly ObjectIDColumns = columnsOfType(ReportColumns.COLUMN_TYPES, ReportCellType.objectId);
  public readonly CurrencyColumns = columnsOfType(ReportColumns.COLUMN_TYPES, ReportCellType.currency);
  public readonly DateColumns = columnsOfType(ReportColumns.COLUMN_TYPES, ReportCellType.date);

  constructor(
    public inboxService: InboxService,
    public orgConfig: OrganizationConfigService,
    public accountService: AccountService,
    private customInboxService: CustomInboxService,
    protected activatedRoute: ActivatedRoute,
    protected router: Router,
    protected savedReportViewService: SavedReportViewService,
    protected modalService: NgbModal
  ) {
    super(
      activatedRoute,
      router,
      savedReportViewService,
      accountService,
      modalService,
    );
  }

  ngOnInit() {
    if (!this.customInboxItemsPageSize) {
      this.customInboxItemsPageSize = 10;
    }
    // This is needed in order for the pageSize to be updated when the html
    // select is first changed.
    // noinspection SillyAssignmentJS
    this.customInboxItemsPageSize = this.customInboxItemsPageSize;

    super.ngOnInit();

    this.loadInboxList();
    this.inboxService.loadInboxCounts(true).then();
  }

  /**
   * Load the page of inbox items corresponding to the current page and filter
   * selections.
   */
  public loadReport() {
    // Normalized here, after report-base's pristine-filters check, rather than
    // seeded in `setInitialFilterParams` (see the note there).
    if (this.filters.is_resolved === undefined) {
      this.filters.is_resolved = false;
    }

    this.loadingInbox = true;

    // Increment the request count and save a local copy.
    this.listRequestCount++;
    const thisRequestCount = this.listRequestCount;
    // The catch below must not read this.pageNumber: by the time a slow
    // rejection lands, a newer request may have moved it.
    const requestedPageNumber = this.pageNumber;

    this.customInboxService
      .getCustomInboxItems(
        requestedPageNumber,
        this.customInboxItemsPageSize,
        this.filters
      )
      .then(response => {
        // Only update the item list if there has not been a more recent request
        // initiated.
        if (thisRequestCount === this.listRequestCount) {
          this.inboxResponse = response;
        }
      })
      .catch(reason => {
        if (reason.status === 404) {
          // The requested page fell past the end of a shrunken result set. A
          // stale 404 must not reset a page a newer request now owns.
          if (thisRequestCount !== this.listRequestCount) {
            return;
          }
          if (requestedPageNumber > 1) {
            // No retry loop: an empty FIRST page is a 200 from Django's
            // paginator, so a second 404 is a real error.
            this.pageNumber = 1;
            this.loadReport();
            return;
          }
        }
        this.serverError = reason.status;
      })
      .finally(() => {
        if (thisRequestCount === this.listRequestCount) {
          this.loadingInbox = false;
        }
      });
  }

  public resultsPresent() {
    return this.inboxResponse && (this.inboxResponse.count > 0);
  }

  /**
   * Get the number of the last page of results.
   */
  public lastPageNumber() {
    if (!this.resultsPresent()) { return 1; }
    return Math.ceil(this.inboxResponse.count / this.customInboxItemsPageSize);
  }

  /**
   * The filters worth naming in a chip. Status and inbox each have their own
   * always-visible select above the table, so chipping them would only restate
   * what the controls already show.
   */
  public getActiveFilters(): string[] {
    const activeFilters: string[] = [];

    const fs = this.filters;

    const createdText = getActiveDateRangeFilterText(
      'Created Date',
      fs.created_at_min,
      fs.created_at_max,
      fs.created_at_range,
    );
    if (createdText !== null) {
      activeFilters.push(createdText);
    }

    if (propExists(fs.customer_id)) {
      activeFilters.push('Customer ID is ' + fs.customer_id.toString());
    }

    return activeFilters;
  }

  /**
   * Open a modal for editing the filters.
   */
  public openEditFiltersModal() {
    if (this.loadingInboxList) { return; }

    this.modalResult = null;

    const modalRef = this.modalService.open(CustomInboxItemFiltersModalComponent);
    modalRef.componentInstance.inputFilters = this.filters;
    modalRef.componentInstance.inboxList = this.inboxList;
    modalRef.componentInstance.savedCallback = (newFilters: CustomInboxItemFilters) => {
      this.filters = newFilters;
      this.handleFilterChange();
    };
  }

  /**
   * Open a modal for exporting the results list, with the currently-selected
   * filters.
   */
  public openExportListModal() {
    this.modalResult = null;

    const modalRef = this.modalService.open(ApiCollectionExportModalComponent);
    modalRef.componentInstance.getPage = (pageNum: number) => {
      return this.customInboxService.getCustomInboxItems(
        pageNum,
        EXPORT_PAGE_SIZE,
        this.filters
      );
    };
    modalRef.componentInstance.getContentItem = ExportColumns.getContentItem;
    modalRef.componentInstance.totalNumPages = Math.ceil(this.inboxResponse.count / EXPORT_PAGE_SIZE);
    modalRef.componentInstance.savedCallback = (result: CallbackResult) => {
      this.modalResult = result;
    };
    modalRef.componentInstance.columnNames = ExportColumns.COLUMN_NAMES;
    modalRef.componentInstance.columnHelpText = ExportColumns.COLUMN_HELP_TEXT;
    modalRef.componentInstance.exportNameHumanReadable = 'Custom Inbox Items';
    modalRef.componentInstance.exportFilenamePrefix = 'Custom_Inbox_Items';
  }

  public openModal(item: CustomInboxItem) {
    this.modalResult = null;

    const modalRef = this.modalService.open(ViewCustomInboxItemComponent);
    modalRef.componentInstance.itemId = item.id;
    modalRef.componentInstance.savedCallback = (result: CallbackResult) => {
      this.modalResult = result;
      this.loadReport();
    };
  }

  protected setInitialFilterParams(replaceFilters: boolean) {
    if (replaceFilters) {
      this.filters = {};
    }

    // No seeding of `is_resolved` here: this runs BEFORE report-base's
    // hasPropNotInList check, and is_resolved is saveable, so a value written
    // here would read as user-edited and block saved-view auto-loading.
    // `loadReport()` applies the unresolved default instead.
    //
    // No default date window either: an item outstanding for months is the
    // one staff need to see, and the count must match the Inbox tab's badge.
    // Emailed runs share this unbounded window (server-side
    // REPORT_DEFAULT_PARAMS) so an email and its deep link agree (PRO-1374).
  }

  /**
   * Whether the selected inbox is missing from the active list and so needs
   * the pinned option. False when no inbox is selected; true while the list
   * is still loading so the select is never blank.
   */
  public selectedInboxInactive(): boolean {
    if (!propExists(this.filters.inbox_id)) { return false; }
    if (!this.inboxList) { return true; }
    return !this.activeInboxes.some(
      inbox => inbox.id === this.filters.inbox_id
    );
  }

  /**
   * The pinned option's label for an inactive selected inbox: its name plus
   * its status (Retired / Draft), mirroring the "(Removed)" convention.
   */
  public selectedInboxDisplay(): string {
    if (!this.inboxList) { return 'Loading...'; }
    const match = this.inboxList.find(
      inbox => inbox.id === this.filters.inbox_id
    );
    if (!match) { return 'Unknown Inbox'; }
    return match.name + ' (' + getCustomInboxStatusDisplay(match.status) + ')';
  }

  private loadInboxList() {
    this.loadingInboxList = true;

    // All statuses, not just active — see the note on inboxList.
    this.customInboxService
      .getCustomInboxes(1, 3000)
      .then(response => {
        this.inboxList = response.results;
        this.activeInboxes = this.inboxList.filter(
          inbox => inbox.status === CustomInboxStatus.active
        );
      })
      .catch(reason => {
        this.serverError = reason.status;
      })
      .finally(() => { this.loadingInboxList = false; });
  }
}
